import { Loading } from "../../app/Status";
import { EstimateSection } from "../mjop/MjopFields";
import { ScreenSection } from "./ScreenSection";
import { useRef, useState } from "react";
import {
  nlsfbLabel,
  optLabel,
  PRIO,
  STATUS,
  typeLabel,
  type BuildingDto,
  type IssueDetail,
  type IssueStatus,
  type TaskDto,
} from "@klantpunt/shared";
import { fmtDue } from "../tasks/TaskBoard";
import { useTaskRefresh } from "../tasks/useTasks";
import { api, ApiError } from "../../lib/api";
import { can, useMe } from "../../lib/auth";
import { isVideo, isVideoFile, photoUrl } from "../../lib/photos";
import { MediaThumb } from "./MediaThumb";
import { addComment, addPhoto, changeStatus, type Result } from "../../offline/issueActions";
import { isLocal } from "../../offline/ops";
import { useToast } from "../../lib/toast";
import { locLabel } from "./filters";
import { useIssue, useIssueCache } from "./useIssues";
import { IconClose } from "../../app/icons";
import { AssetBadge } from "../assets/assetIcons";
import { LogList } from "../logbook/Logbook";
import { MentionComposer } from "../logbook/MentionComposer";

const fmtDT = (iso: string) =>
  new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) +
  " " +
  new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });

export interface DetailActions {
  onClose: () => void;
  /** Asset van deze bevinding openen. */
  onOpenAsset?: (assetId: string) => void;
  onEdit: (d: IssueDetail) => void;
  /** Toon een plek op de tekening (verdieping wisselen en centreren). */
  onShow: (loc: { floorId: string; x: number; y: number }, issueId: string) => void;
  /** Volgende tik op de tekening verplaatst de hoofdpin. */
  onMovePin: (d: IssueDetail) => void;
  /** Plek toevoegen (dialoog: zonder pin of aanwijzen op tekening). */
  onAddLocation: (d: IssueDetail) => void;
  /** Koppel aan taak (nieuw of bestaand). */
  onLinkTask: (d: IssueDetail) => void;
  /** Naar het takenoverzicht. */
  onOpenTasks: () => void;
}

/** Detail van een bevinding (SPEC §5). */
export function IssueDetailPanel({
  building: b,
  issueId,
  tasks,
  actions,
}: {
  building: BuildingDto;
  issueId: string;
  tasks: TaskDto[];
  actions: DetailActions;
}) {
  const { data: d, isPending } = useIssue(issueId, b.id);
  const { data: me } = useMe();
  const cache = useIssueCache(b.id);
  const toast = useToast();
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);

  const canEdit = can(me, "issue.edit");
  const canStatus = can(me, "issue.status");
  const canComment = can(me, "issue.comment");
  const canPhoto = can(me, "issue.photo");
  const canDelete = can(me, "issue.delete");
  const canTask = can(me, "task.manage");
  const refreshTasks = useTaskRefresh(b.id);

  async function run(fn: () => Promise<IssueDetail | void>, ok?: string) {
    setBusy(true);
    try {
      const out = await fn();
      if (out) cache.put(out);
      if (ok) toast(ok);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Er ging iets mis");
    } finally {
      setBusy(false);
    }
  }

  /** Ook offline (SPEC §11): via de wachtrij; zonder verbinding een melding dat het later wordt verstuurd. */
  async function runQueued(fn: () => Promise<Result>, ok?: string) {
    setBusy(true);
    try {
      const r = await fn();
      // In de wachtrij: de weergave legt de wijziging zelf over de servergegevens heen.
      if (!r.queued) cache.put(r.issue);
      if (r.queued) toast("Opgeslagen op dit apparaat; wordt verstuurd zodra er verbinding is");
      else if (ok) toast(ok);
      return true;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Er ging iets mis");
      return false;
    } finally {
      setBusy(false);
    }
  }
  const meRef = { id: me?.user.id ?? "", name: me?.user.name ?? me?.user.email ?? "" };

  if (isPending || !d) {
    return (
      <div className="detail" role="region" aria-label="Bevinding">
        <Loading page />
      </div>
    );
  }

  /** Foto's of video's toevoegen (via de wachtrij, werkt ook zonder verbinding). */
  function addMedia(input: HTMLInputElement) {
    const list = [...(input.files ?? [])];
    input.value = "";
    if (!list.length) return;
    const videos = list.filter(isVideoFile).length;
    const what =
      list.length === 1
        ? videos
          ? "Video"
          : "Foto"
        : videos === list.length
          ? `${list.length} video's`
          : videos
            ? `${list.length} bestanden`
            : `${list.length} foto's`;
    void runQueued(async () => {
      let r: Result = { issue: d!, queued: false };
      let queued = false;
      for (const f of list) {
        r = await addPhoto(r.issue, b.id, f);
        queued ||= r.queued;
      }
      return { issue: r.issue, queued };
    }, `${what} toegevoegd`);
  }

  // Nog niet verstuurd: bewerken, plekken, taak en verwijderen pas als de server de bevinding kent.
  const local = isLocal(d);
  const editable = canEdit && !local;
  const main = d.locations[0];
  const mainFloor = b.floors.find((f) => f.id === main?.floorId);
  const room = b.rooms.find((r) => r.id === main?.roomId);
  const inst = b.assets.find((x) => x.id === d.installationId);
  const a = d.answers;

  return (
    <div className="detail" role="region" aria-label={`Bevinding ${d.code}`}>
      <div className="dhead">
        <div className="row">
          <span className="code">{d.code}</span>
          <span className={`chip prio prio-${d.priority}`}>
            {d.priority} · {PRIO[d.priority].l}
          </span>
          {isLocal(d) && <span className="chip pending">Wacht op verbinding</span>}
          <span className="grow" />
          <button type="button" className="btn ghost small" onClick={actions.onClose} aria-label="Sluiten">
            <IconClose size={20} aria-hidden="true" />
          </button>
        </div>
        <div className="row">
          <select
            aria-label="Status"
            className={`statussel st-${d.status}`}
            value={d.status}
            disabled={!canStatus || busy}
            onChange={(e) => runQueued(() => changeStatus(d, b.id, e.target.value as IssueStatus))}
          >
            {STATUS.map((s) => (
              <option key={s.k} value={s.k}>
                {s.l}
              </option>
            ))}
          </select>
          <span className="grow" />
          {editable && (
            <button type="button" className="btn small" onClick={() => actions.onEdit(d)}>
              Bewerken
            </button>
          )}
        </div>
      </div>
      <div className="dbody">
        <section className="sec">
          <h2 className="dtitle">{d.title}</h2>
          <div className="crumbs">
            <strong>{b.name}</strong> › <span>{mainFloor?.name ?? "—"}</span>
            {room && (
              <>
                {" "}
                › <span>{room.name}</span>
              </>
            )}
            {inst && (
              <>
                {" "}
                ›{" "}
                {actions.onOpenAsset ? (
                  <button type="button" className="linkbtn" onClick={() => actions.onOpenAsset!(inst.id)}>
                    {inst.code} {inst.name}
                  </button>
                ) : (
                  <span>
                    {inst.code} {inst.name}
                  </span>
                )}
              </>
            )}
          </div>
          {d.elementCode && (
            <p className="elementline" data-testid="issue-element">
              <AssetBadge code={d.elementCode} size={22} />
              <span>{nlsfbLabel(d.elementCode)}</span>
            </p>
          )}
          {d.description && <p className="prewrap">{d.description}</p>}
          <div className="btnrow">
            {main?.x != null && main.y != null && (
              <button
                type="button"
                className="btn small"
                onClick={() => actions.onShow({ floorId: main.floorId, x: main.x!, y: main.y! }, d.id)}
              >
                Toon op tekening
              </button>
            )}
            {editable && mainFloor?.plan && (
              <button type="button" className="btn small" onClick={() => actions.onMovePin(d)}>
                {main?.x != null ? "Hoofdpin verplaatsen" : "Pin plaatsen"}
              </button>
            )}
          </div>
        </section>

        <section className="sec">
          <p className="label">
            Plekken <span className="num">{d.locations.length}</span>
          </p>
          <ul className="locs">
            {d.locations.map((l, k) => (
              <li key={l.id}>
                <span className="n num">{k + 1}</span>
                <span className="grow">
                  {locLabel(b, l)}
                  {k === 0 && <span className="hint"> hoofdplek</span>}
                </span>
                {l.x != null && l.y != null ? (
                  <button
                    type="button"
                    className="btn small"
                    onClick={() => actions.onShow({ floorId: l.floorId, x: l.x!, y: l.y! }, d.id)}
                  >
                    Toon
                  </button>
                ) : (
                  <span className="hint">geen pin</span>
                )}
                {editable && k > 0 && (
                  <button
                    type="button"
                    className="btn small ghost"
                    aria-label={`Plek ${k + 1} verwijderen`}
                    onClick={() =>
                      run(() => api<IssueDetail>(`/issues/${d.id}/locations/${l.id}`, { method: "DELETE" }))
                    }
                  >
                    <IconClose size={18} aria-hidden="true" />
                  </button>
                )}
              </li>
            ))}
          </ul>
          {editable && (
            <button type="button" className="btn small" onClick={() => actions.onAddLocation(d)}>
              + Komt ook elders voor
            </button>
          )}
        </section>

        <section className="sec">
          <p className="label">Beoordeling</p>
          <div className="prioexp">
            <span className={`big prio-${d.priority}`}>{d.priority}</span>
            <span className="txt">
              <strong>
                {PRIO[d.priority].l}: {PRIO[d.priority].t}
              </strong>
              <br />
              Berekend uit gevaar, impact en verergering.
            </span>
          </div>
          <dl className="kv">
            <dt>Type</dt>
            <dd>{typeLabel(a.type)}</dd>
            <dt>Gevaar</dt>
            <dd>{optLabel("danger", a.danger)}</dd>
            <dt>Impact</dt>
            <dd>{optLabel("impact", a.impact)}</dd>
            <dt>Verergert</dt>
            <dd>{optLabel("worsen", a.worsen)}</dd>
            <dt>Omvang</dt>
            <dd>{optLabel("size", a.size)}</dd>
            <dt>Oplosser</dt>
            <dd>{optLabel("solver", a.solver)}</dd>
            <dt>Herkomst</dt>
            <dd>{optLabel("origin", a.origin)}</dd>
            <dt>Vastgelegd</dt>
            <dd>
              {fmtDT(d.createdAt)}
              {d.createdBy && ` door ${d.createdBy.name}`}
            </dd>
          </dl>
        </section>

        {!d.code.endsWith("…") && <EstimateSection issueId={d.id} priority={d.priority} />}
        {!d.code.endsWith("…") && <ScreenSection issueId={d.id} title={d.title} status={d.status} />}

        <section className="sec">
          <p className="label">Foto's en video's</p>
          <div className="photos">
            {d.photos.map((ph) => (
              <a className="ph" key={ph.fileId} href={photoUrl(ph.fileId)} target="_blank" rel="noopener">
                <MediaThumb
                  fileId={ph.fileId}
                  mime={ph.mime}
                  alt={`${isVideo(ph.fileId, ph.mime) ? "Video" : "Foto"} bij ${d.code}`}
                />
              </a>
            ))}
            {canPhoto && (
              <>
                <button type="button" className="add" onClick={() => photoInput.current?.click()} disabled={busy}>
                  +<br />
                  Foto toevoegen
                </button>
                <button type="button" className="add" onClick={() => videoInput.current?.click()} disabled={busy}>
                  +<br />
                  Video toevoegen
                </button>
              </>
            )}
            {!canPhoto && d.photos.length === 0 && <span className="hint">Geen foto's of video's.</span>}
          </div>
          {/* Zonder capture: op de telefoon kiest de gebruiker zelf tussen opnemen en een bestaande foto of video. */}
          <input
            ref={photoInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            data-testid="detail-photo-input"
            onChange={(e) => addMedia(e.target)}
          />
          <input
            ref={videoInput}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/*"
            multiple
            hidden
            data-testid="detail-video-input"
            onChange={(e) => addMedia(e.target)}
          />
        </section>

        <section className="sec">
          <p className="label">Taak</p>
          {(() => {
            const t = tasks.find((x) => x.id === d.taskId);
            if (t) {
              return (
                <div className="linkbox">
                  <div>
                    <span className="code small">{t.code}</span> <strong>{t.title}</strong>
                  </div>
                  <span className="hint">
                    {t.assigneeName ?? "Nog niet toegewezen"}
                    {t.dueDate && ` · uiterlijk ${fmtDue(t.dueDate)}`} · {t.issueIds.length} bevinding(en)
                  </span>
                  <div className="btnrow">
                    <button type="button" className="btn small" onClick={actions.onOpenTasks}>
                      Naar taak
                    </button>
                    {canTask && !local && (
                      <button
                        type="button"
                        className="btn small"
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            await api(`/tasks/${t.id}`, {
                              method: "PATCH",
                              body: { issueIds: t.issueIds.filter((x) => x !== d.id) },
                            });
                            await refreshTasks();
                          }, `Ontkoppeld van taak ${t.code}`)
                        }
                      >
                        Ontkoppelen
                      </button>
                    )}
                  </div>
                </div>
              );
            }
            return canTask && !local ? (
              <button type="button" className="btn small" onClick={() => actions.onLinkTask(d)}>
                Koppel aan taak
              </button>
            ) : (
              <span className="hint">Niet gekoppeld.</span>
            );
          })()}
        </section>

        <section className="sec">
          <p className="label">Robaws</p>
          <span className="hint">Nog niet gekoppeld aan Robaws.</span>
        </section>

        <section className="sec">
          <p className="label">
            Logboek <span className="num">{d.events.length}</span>
          </p>
          {canComment && (
            <MentionComposer
              id={`c-${d.id}`}
              entity="issue"
              targetId={d.id}
              buildingId={b.id}
              busy={busy}
              onSubmit={(body) => runQueued(() => addComment(d, b.id, body, meRef))}
            />
          )}
          <LogList entries={d.events} />
        </section>

        {canDelete && !local && (
          <section className="sec">
            {confirmDel ? (
              <div className="note danger-note" role="alert">
                Bevinding {d.code} definitief verwijderen?{" "}
                <button
                  type="button"
                  className="btn small danger"
                  disabled={busy}
                  onClick={() =>
                    run(async () => {
                      await api(`/issues/${d.id}`, { method: "DELETE" });
                      cache.remove(d.id);
                      actions.onClose();
                    }, `${d.code} verwijderd`)
                  }
                >
                  Verwijderen
                </button>{" "}
                <button type="button" className="btn small" onClick={() => setConfirmDel(false)}>
                  Annuleren
                </button>
              </div>
            ) : (
              <button type="button" className="btn small danger" onClick={() => setConfirmDel(true)}>
                Verwijderen
              </button>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
